/**
 * Data Service Layer
 * 数据服务层 - 封装所有数据的 CRUD 操作
 */

import {
    Recipe,
    IngredientDetail,
    ShoppingItem,
    UserProfile,
    ScheduleByDate,
    UserInventory,
    InventoryItem,
    StockBatch,
    UserRecipe,
    UserIngredient,
    LogEntry,
    UserFavorite,
    AIConfig,
    UserMealTimeConfig,
    Gender,
    ActivityLevel,
    GoalType,
    StorageError,
    ValidationError,
    BusinessError,
    RECIPE_TAG_TAXONOMY,
} from '../types';
import {
    storageAdapter,
    StorageAdapter,
    StorageKeys,
    LocalStorageAdapter,
} from '../utils/storageAdapter';
import {
    initialUserProfile,
    initialRecipes,
} from '../data';
import { LRUCache, TTL_PRESETS } from '../utils/lruCache';
import { PersistentCacheService } from '../utils/persistentCache';
import { apiRequest, isApiConfigured } from '../utils/apiClient';


// 专门的 LocalStorage 实例用于存储敏感数据（如 AI 配置）
// 注意：不需要prefix，因为StorageKeys已经包含了完整的key格式
const localStorageAdapter = new LocalStorageAdapter('');

// 全局持久化缓存实例（L2 缓存）
// 开发环境和生产环境都启用缓存，开发模式下会输出调试日志
const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
const persistentCache = new PersistentCacheService({
    enabled: true,  // 始终启用缓存
    debug: isDev    // 仅开发模式输出详细日志
});

// 内存缓存（L1 缓存）- 使用 LRU + TTL
const memoryCache = new LRUCache<string, any>({
    maxSize: 100,
    defaultTtl: TTL_PRESETS.MEDIUM
});

/**
 * 数据服务类
 * 提供统一的数据访问层，支持 localStorage 与自托管 HTTP API
 * 
 * 三级缓存架构：
 * L1: memoryCache - 内存缓存，最快，页面刷新后丢失
 * L2: persistentCache - LocalStorage 持久化缓存，页面刷新后可恢复
 * L3: adapter - LocalStorage / HTTP API 存储，最终数据源
 */
class DataService {
    private adapter: StorageAdapter;
    private userId: string;

    constructor(adapter: StorageAdapter, userId: string = StorageKeys.DEFAULT_USER_ID) {
        this.adapter = adapter;
        this.userId = userId;
    }

    /**
     * 包装异步操作以统一错误处理
     */
    private async wrapOperation<T>(
        operation: () => Promise<T>,
        operationName: string,
        context?: any
    ): Promise<T> {
        try {
            return await operation();
        } catch (error) {
            // 转换为业务错误
            if (error instanceof ValidationError || error instanceof BusinessError) {
                throw error;
            }

            // 其他错误包装为存储错误
            throw new StorageError(
                `数据操作失败: ${operationName}`,
                'other',
                {
                    operation: operationName,
                    userId: this.userId,
                    originalError: error,
                    ...context,
                }
            );
        }
    }

    /**
     * 设置用户 ID（用于多用户支持）
     */
    setUserId(userId: string): void {
        this.userId = userId;
        memoryCache.clear();
        persistentCache.clear(); // 切换用户时清除缓存
    }

    /**
     * 获取当前用户 ID
     */
    getUserId(): string {
        return this.userId;
    }

    private async updateUserProfileProjection(
        userId: string,
        updater: (profile: UserProfile) => UserProfile
    ): Promise<void> {
        const profileKey = StorageKeys.userProfile(userId);
        const existingProfile = await this.adapter.get<UserProfile>(profileKey);
        const baseProfile: UserProfile = existingProfile
            ? { ...existingProfile, id: userId }
            : { ...initialUserProfile, id: userId };

        const updatedProfile = updater(baseProfile);
        await this.adapter.set(profileKey, updatedProfile);

        if (userId === this.userId) {
            memoryCache.set(profileKey, updatedProfile, TTL_PRESETS.SHORT);
            persistentCache.set(profileKey, updatedProfile, TTL_PRESETS.MEDIUM);
        }
    }

    /**
     * 检查当前是否为未登录的匿名用户
     */
    isAnonymous(): boolean {
        return this.userId === 'anonymous';
    }

    private normalizeIngredientName(value: string): string {
        return value
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/[()（）\[\]【】\-_,.，。:：;'"]/g, '')
            .replace(/\d+$/g, '');
    }

    private extractIngredientNameVariants(value: string): string[] {
        const variants = new Set<string>();
        const trimmed = value.trim();
        if (!trimmed) {
            return [];
        }

        variants.add(trimmed);

        const withoutBrackets = trimmed.replace(/[\(\[（【].*?[\)\]）】]/g, ' ').trim();
        if (withoutBrackets) {
            variants.add(withoutBrackets);
        }

        const bracketMatches = trimmed.match(/[\(\[（【]([^()\[\]（）【】]+)[\)\]）】]/g) || [];
        for (const rawMatch of bracketMatches) {
            const inner = rawMatch.slice(1, -1).trim();
            if (inner) {
                variants.add(inner);
            }
        }

        const splitMatches = trimmed
            .split(/[\/、,，;；]/)
            .map(part => part.trim())
            .filter(Boolean);
        for (const part of splitMatches) {
            variants.add(part);
        }

        return Array.from(variants);
    }

    private getIngredientLookupTerms(ingredient: Pick<IngredientDetail, 'name' | 'aliasNames' | 'englishName' | 'tags'> | Pick<UserIngredient, 'name' | 'aliasNames' | 'englishName' | 'tags'>): string[] {
        return [
            ...this.extractIngredientNameVariants(ingredient.name),
            ...(ingredient.aliasNames || []).flatMap(alias => this.extractIngredientNameVariants(alias)),
            ...this.extractIngredientNameVariants(ingredient.englishName || ''),
            ...(ingredient.tags || []).flatMap(tag => this.extractIngredientNameVariants(tag)),
        ].filter(Boolean);
    }

    private shouldIgnoreEmptyPublicCache<T>(cacheKey: string, value: T[] | null): boolean {
        if (!Array.isArray(value) || value.length > 0) {
            return false;
        }

        return isApiConfigured() && (
            cacheKey === StorageKeys.PUBLIC_RECIPES ||
            cacheKey === StorageKeys.PUBLIC_INGREDIENTS
        );
    }

    private async queryPublicCatalog<T>(
        type: 'recipes' | 'ingredients',
        params: Record<string, unknown>
    ): Promise<{ items: T[]; total: number }> {
        if (isApiConfigured()) {
            return apiRequest<{ items: T[]; total: number }>('/api/catalog/query', {
                method: 'POST',
                body: JSON.stringify({ type, params }),
            });
        }

        const key = type === 'recipes' ? StorageKeys.PUBLIC_RECIPES : StorageKeys.PUBLIC_INGREDIENTS;
        return this.adapter.query<T>(key, params as never);
    }

    private async getPublicCatalogEntry<T>(
        type: 'recipes' | 'ingredients',
        id: string
    ): Promise<T | null> {
        if (isApiConfigured()) {
            try {
                return await apiRequest<T>(`/api/catalog/${type}/${encodeURIComponent(id)}`);
            } catch (error) {
                if (error instanceof Error && /404/.test(error.message)) {
                    return null;
                }
                throw error;
            }
        }

        const entries = (type === 'recipes'
            ? await this.getRecipes()
            : await this.getIngredients()) as Array<{ id: string }>;
        return (entries.find((entry) => entry.id === id) as T | undefined) || null;
    }

    // ========== 公共数据操作 ==========

    /**
     * 获取所有食谱
     * 公共数据，优先从缓存/存储读取，否则返回初始数据
     */
    async getRecipes(): Promise<Recipe[]> {
        const cacheKey = StorageKeys.PUBLIC_RECIPES;

        // L1: 检查内存缓存
        const l1Cache = memoryCache.get(cacheKey) as Recipe[] | null;
        if (l1Cache !== null && !this.shouldIgnoreEmptyPublicCache(cacheKey, l1Cache)) {
            return l1Cache;
        }
        if (this.shouldIgnoreEmptyPublicCache(cacheKey, l1Cache)) {
            memoryCache.delete(cacheKey);
        }

        // L2: 检查持久化缓存
        const l2Cache = persistentCache.get(cacheKey) as Recipe[] | null;
        if (l2Cache !== null && !this.shouldIgnoreEmptyPublicCache(cacheKey, l2Cache)) {
            // 回填 L1 缓存
            memoryCache.set(cacheKey, l2Cache, TTL_PRESETS.MEDIUM);
            return l2Cache;
        }
        if (this.shouldIgnoreEmptyPublicCache(cacheKey, l2Cache)) {
            persistentCache.delete(cacheKey);
        }

        // L3: 从数据库加载
        try {
            const saved = await this.adapter.get<Recipe[]>(cacheKey);
            const recipes = saved && saved.length > 0 ? saved : initialRecipes;
            
            // 填充 L1 和 L2 缓存
            memoryCache.set(cacheKey, recipes, TTL_PRESETS.MEDIUM);
            persistentCache.set(cacheKey, recipes, TTL_PRESETS.LONG);
            
            return recipes;
        } catch (error) {
            console.error('[DataService] getRecipes error:', error);
            return initialRecipes;
        }
    }

    /**
     * 保存食谱数据
     */
    async saveRecipes(recipes: Recipe[]): Promise<void> {
        const publicRecipes = recipes.filter(recipe => !recipe.ownerId);
        await this.adapter.set(StorageKeys.PUBLIC_RECIPES, publicRecipes);
        // 同时更新 L1 和 L2 缓存
        memoryCache.set(StorageKeys.PUBLIC_RECIPES, publicRecipes, TTL_PRESETS.MEDIUM);
        persistentCache.set(StorageKeys.PUBLIC_RECIPES, publicRecipes, TTL_PRESETS.LONG);
    }

    /**
     * 获取所有食材
     */
    async getIngredients(): Promise<IngredientDetail[]> {
        const cacheKey = StorageKeys.PUBLIC_INGREDIENTS;

        // L1: 检查内存缓存
        const l1Cache = memoryCache.get(cacheKey) as IngredientDetail[] | null;
        if (l1Cache !== null && !this.shouldIgnoreEmptyPublicCache(cacheKey, l1Cache)) {
            return l1Cache;
        }
        if (this.shouldIgnoreEmptyPublicCache(cacheKey, l1Cache)) {
            memoryCache.delete(cacheKey);
        }

        // L2: 检查持久化缓存
        const l2Cache = persistentCache.get(cacheKey) as IngredientDetail[] | null;
        if (l2Cache !== null && !this.shouldIgnoreEmptyPublicCache(cacheKey, l2Cache)) {
            memoryCache.set(cacheKey, l2Cache, TTL_PRESETS.MEDIUM);
            return l2Cache;
        }
        if (this.shouldIgnoreEmptyPublicCache(cacheKey, l2Cache)) {
            persistentCache.delete(cacheKey);
        }

        // L3: 从数据库加载
        const saved = await this.adapter.get<IngredientDetail[]>(cacheKey);
        const ingredients = saved || [];
        
        // 填充 L1 和 L2 缓存
        memoryCache.set(cacheKey, ingredients, TTL_PRESETS.MEDIUM);
        persistentCache.set(cacheKey, ingredients, TTL_PRESETS.LONG);
        
        return ingredients;
    }

    /**
     * 分页查询食材
     */
    async getIngredientsPaginated(options: {
        page: number;
        pageSize: number;
        category?: string;
        subCategory?: string;
        searchQuery?: string;
        sortBy?: 'default' | 'name' | 'calories';
        sortOrder?: 'asc' | 'desc';
        includeIds?: string[];
        excludeIds?: string[];
    }): Promise<{ items: IngredientDetail[]; total: number }> {
        return this.queryPublicCatalog<IngredientDetail>('ingredients', options);
    }

    async getRecipesPaginated(options: {
        page: number;
        pageSize: number;
        searchQuery?: string;
        tagGroups?: string[][];
        sortBy?: 'default' | 'name' | 'time' | 'difficulty' | 'calories';
        sortOrder?: 'asc' | 'desc';
        includeIds?: string[];
        excludeIds?: string[];
    }): Promise<{ items: Recipe[]; total: number }> {
        return this.queryPublicCatalog<Recipe>('recipes', options);
    }

    /**
     * 获取食材分类层级结构 (基于真实数据)
     */
    async getIngredientTaxonomy(): Promise<{ id: string; label: string; subCategories: string[] }[]> {
        const cacheKey = 'foodbook:taxonomy:ingredients';
        
        // L1: 检查内存缓存
        const l1Cache = memoryCache.get(cacheKey);
        if (l1Cache !== null) {
            return l1Cache;
        }

        // 真实业务数据分类（来源于数据库 DISTINCT 查询结果）
        const taxonomy = [
            { id: '全部', label: '全部', subCategories: [] },
            { id: '谷类及制品', label: '谷类及制品', subCategories: ['小麦', '稻米', '玉米', '大麦', '小米、黄米', '其它'] },
            { id: '薯类、淀粉及制品', label: '薯类、淀粉及制品', subCategories: ['薯类', '淀粉类'] },
            { id: '干豆类及制品', label: '干豆类及制品', subCategories: ['大豆', '绿豆', '赤豆', '芸豆', '蚕豆', '其它'] },
            { id: '蔬菜类及制品', label: '蔬菜类及制品', subCategories: ['根菜类', '鲜豆类', '茄果、瓜菜类', '葱蒜类', '嫩茎、叶、花菜类', '水生蔬菜类', '薯芋类', '野生蔬菜类', '其它'] },
            { id: '菌藻类', label: '菌藻类', subCategories: ['菌类', '藻类'] },
            { id: '水果类及制品', label: '水果类及制品', subCategories: ['仁果类', '核果类', '浆果类', '柑橘类', '瓜类', '热带水果', '其它'] },
            { id: '坚果、种子类', label: '坚果、种子类', subCategories: ['坚果类', '种子类'] },
            { id: '畜肉类及制品', label: '畜肉类及制品', subCategories: ['猪肉', '牛肉', '羊肉', '其它'] },
            { id: '禽肉类及制品', label: '禽肉类及制品', subCategories: ['鸡', '鸭', '鹅', '其它'] },
            { id: '乳类及制品', label: '乳类及制品', subCategories: ['鲜乳类', '乳制品'] },
            { id: '蛋类及制品', label: '蛋类及制品', subCategories: ['蛋类', '蛋制品'] },
            { id: '鱼虾蟹贝类', label: '鱼虾蟹贝类', subCategories: ['鱼类', '虾类', '蟹类', '贝类', '其它'] },
            { id: '婴幼儿食品', label: '婴幼儿食品', subCategories: ['配方粉类', '其它'] },
            { id: '速食食品', label: '速食食品', subCategories: ['方便食品', '休闲食品'] },
            { id: '饮料类', label: '饮料类', subCategories: ['碳酸饮料', '果汁类及饮料', '蔬菜汁类及饮料', '含乳饮料', '植物蛋白饮料', '固体饮料', '棒冰、冰淇凌类', '茶叶及茶饮料'] },
            { id: '含酒精饮料', label: '含酒精饮料', subCategories: ['发酵酒', '蒸馏酒', '露酒（配制酒）'] },
            { id: '糖、果脯和蜜饯、蜂蜜', label: '糖、果脯和蜜饯、蜂蜜', subCategories: ['糖', '糖果', '果脯和蜜饯', '蜂蜜'] },
            { id: '调味品类', label: '调味品类', subCategories: ['酱油', '醋', '酱', '腐乳', '咸菜类', '辛香料', '盐、味精及其它'] },
            { id: '油脂类', label: '油脂类', subCategories: ['动物油脂', '植物油'] },
            { id: '其它', label: '其它', subCategories: [] }
        ];

        // 填充 L1 缓存
        memoryCache.set(cacheKey, taxonomy, TTL_PRESETS.LONG);
        return taxonomy;
    }

    /**
     * 保存食材数据
     */
    async saveIngredients(
        ingredients: IngredientDetail[],
        options: { allowEmpty?: boolean } = {}
    ): Promise<void> {
        if (ingredients.length === 0 && !options.allowEmpty && isApiConfigured()) {
            const existingIngredients = await this.adapter.get<IngredientDetail[]>(StorageKeys.PUBLIC_INGREDIENTS);
            if (Array.isArray(existingIngredients) && existingIngredients.length > 0) {
                console.warn('[DataService] Refusing to overwrite non-empty public ingredients with an empty payload');
                memoryCache.set(StorageKeys.PUBLIC_INGREDIENTS, existingIngredients, TTL_PRESETS.MEDIUM);
                persistentCache.set(StorageKeys.PUBLIC_INGREDIENTS, existingIngredients, TTL_PRESETS.LONG);
                return;
            }
        }

        await this.adapter.set(StorageKeys.PUBLIC_INGREDIENTS, ingredients);
        // 同时更新 L1 和 L2 缓存
        memoryCache.set(StorageKeys.PUBLIC_INGREDIENTS, ingredients, TTL_PRESETS.MEDIUM);
        persistentCache.set(StorageKeys.PUBLIC_INGREDIENTS, ingredients, TTL_PRESETS.LONG);
    }

    /**
     * 批量更新/插入食材
     */
    async upsertIngredients(newIngredients: IngredientDetail[]): Promise<void> {
        const currentIngredients = await this.getIngredients();
        const updatedIngredients = [...currentIngredients];

        newIngredients.forEach(ing => {
            const index = updatedIngredients.findIndex(i => i.id === ing.id);
            if (index !== -1) {
                updatedIngredients[index] = ing;
            } else {
                updatedIngredients.push(ing);
            }
        });

        await this.saveIngredients(updatedIngredients);
    }

    // ========== 用户数据操作 ==========

    /**
     * 获取用户配置
     */
    async getUserProfile(): Promise<UserProfile> {
        const key = StorageKeys.userProfile(this.userId);

        // L1: 检查内存缓存
        const l1Cache = memoryCache.get(key) as UserProfile | null;
        if (l1Cache !== null) {
            return l1Cache;
        }

        // L2: 检查持久化缓存（用户数据 TTL 较短）
        const l2Cache = persistentCache.get(key) as UserProfile | null;
        if (l2Cache !== null) {
            memoryCache.set(key, l2Cache, TTL_PRESETS.SHORT);
            return l2Cache;
        }

        if (this.isAnonymous()) {
            const profile = { ...initialUserProfile, id: this.userId };
            memoryCache.set(key, profile, TTL_PRESETS.SHORT);
            return profile;
        }

        const [saved, favorites] = await Promise.all([
            this.adapter.get<UserProfile>(key),
            this.getUserFavorites()
        ]);

        const favoriteRecipeIds = favorites
            .filter(fav => fav.type === 'recipe')
            .map(fav => fav.itemId);
        const favoriteIngredientIds = favorites
            .filter(fav => fav.type === 'ingredient')
            .map(fav => fav.itemId);

        // 确保 profile 的 id 与当前 userId 一致，避免主键冲突
        const profile = saved 
            ? { 
                ...saved, 
                id: this.userId,
                favoriteRecipeIds: Array.from(new Set([...(saved.favoriteRecipeIds || []), ...favoriteRecipeIds])),
                favoriteIngredientIds: Array.from(new Set([...(saved.favoriteIngredientIds || []), ...favoriteIngredientIds]))
              } 
            : { 
                ...initialUserProfile, 
                id: this.userId,
                favoriteRecipeIds,
                favoriteIngredientIds
              };

        memoryCache.set(key, profile);
        // 填充 L1 和 L2 缓存（用户数据 TTL 较短）
        memoryCache.set(key, profile, TTL_PRESETS.SHORT);
        persistentCache.set(key, profile, TTL_PRESETS.MEDIUM);
        return profile;
    }

    /**
     * 保存用户配置
     */
    async saveUserProfile(profile: UserProfile): Promise<void> {
        const key = StorageKeys.userProfile(this.userId);
        if (!this.isAnonymous()) {
            await this.adapter.set(key, profile);
        }
        // 同时更新 L1 和 L2 缓存
        memoryCache.set(key, profile, TTL_PRESETS.SHORT);
        persistentCache.set(key, profile, TTL_PRESETS.MEDIUM);
    }

    /**
     * 保存用户偏好设置（BMI增强功能）
     * 专门用于保存用户目标和偏好数据
     */
    async saveUserPreferences(preferences: {
        bmi?: number;
        bmiCategory?: 'underweight' | 'normal' | 'overweight' | 'obese';
        bmr?: number;
        tdee?: number;
        gender?: Gender;
        birthdate?: string;
        cookingTools?: string[];
        activityLevel?: ActivityLevel;
        goalType?: GoalType;
        cuisinePreferences?: string[];
        tastePreferences?: string[];
        difficultyPreference?: 'quick' | 'home' | 'gourmet';
        goals?: UserProfile['goals'];
        allergies?: string[];
        dietaryRestrictions?: string[];
    }): Promise<void> {
        const key = StorageKeys.userProfile(this.userId);
        
        console.log('[DataService] saveUserPreferences 开始, userId:', this.userId, 'isAnonymous:', this.isAnonymous());
        
        // 获取当前配置
        const currentProfile = await this.getUserProfile();
        console.log('[DataService] 当前配置:', currentProfile);
        
        // 合并新偏好
        const updatedProfile: UserProfile = {
            ...currentProfile,
            ...preferences,
            // 确保嵌套对象正确合并
            goals: preferences.goals ? { ...currentProfile.goals, ...preferences.goals } : currentProfile.goals,
            updatedAt: new Date().toISOString()
        };
        
        console.log('[DataService] 合并后配置:', updatedProfile);
        
        // 验证数据
        try {
            this.validateUserProfile(updatedProfile);
            console.log('[DataService] 数据验证通过');
        } catch (validationError) {
            console.error('[DataService] 数据验证失败:', validationError);
            throw validationError;
        }
        
        // 保存到存储
        if (!this.isAnonymous()) {
            console.log('[DataService] 正在保存到适配器, key:', key);
            try {
                await this.adapter.set(key, updatedProfile);
                console.log('[DataService] 适配器保存成功');
            } catch (adapterError) {
                console.error('[DataService] 适配器保存失败:', adapterError);
                throw adapterError;
            }
        } else {
            console.log('[DataService] 匿名用户，跳过适配器保存');
        }
        
        memoryCache.set(key, updatedProfile);
        console.log('[DataService] 缓存已更新');
    }

    /**
     * 验证用户配置数据
     * @throws ValidationError 当数据无效时
     */
    validateUserProfile(profile: UserProfile): void {
        // 验证身高范围
        if (profile.goals?.height !== undefined) {
            if (profile.goals.height < 50 || profile.goals.height > 300) {
                throw new ValidationError('身高必须在 50-300 cm 之间');
            }
        }
        
        // 验证体重范围
        if (profile.goals?.weight !== undefined) {
            if (profile.goals.weight < 20 || profile.goals.weight > 500) {
                throw new ValidationError('体重必须在 20-500 kg 之间');
            }
        }
        
        // 验证BMI范围
        if (profile.bmi !== undefined) {
            if (profile.bmi < 10 || profile.bmi > 60) {
                throw new ValidationError('BMI值异常，请检查身高体重数据');
            }
        }
        
        // 验证出生日期格式
        if (profile.birthdate) {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(profile.birthdate)) {
                throw new ValidationError('出生日期格式无效，应为 YYYY-MM-DD');
            }
            
            const birthDate = new Date(profile.birthdate);
            const today = new Date();
            if (birthDate > today) {
                throw new ValidationError('出生日期不能是未来日期');
            }
            
            const age = today.getFullYear() - birthDate.getFullYear();
            if (age > 150) {
                throw new ValidationError('出生日期异常，年龄超过150岁');
            }
        }
        
        // 验证烹饪用具
        if (profile.cookingTools) {
            const validTools = RECIPE_TAG_TAXONOMY.find(cat => cat.id === 'tool')?.subCategories || [];
            const invalidTools = profile.cookingTools.filter(tool => !validTools.includes(tool));
            if (invalidTools.length > 0) {
                console.warn('[DataService] 无效的烹饪用具:', invalidTools);
                // 过滤掉无效用具而不是报错
                profile.cookingTools = profile.cookingTools.filter(tool => validTools.includes(tool));
            }
        }
        
        // 验证营养目标最低安全摄入量
        if (profile.goals?.targetCalories) {
            const minCalories = profile.gender === 'female' ? 1200 : 1500;
            if (profile.goals.targetCalories < minCalories) {
                throw new ValidationError(
                    `卡路里目标过低，${profile.gender === 'female' ? '女性' : '男性'}最低安全摄入量为 ${minCalories} kcal`
                );
            }
        }
    }

    /**
     * 检查本地是否有用户数据（用于迁移检测）
     */
    async checkLocalDataExists(): Promise<{ exists: boolean; timestamp?: string }> {
        const key = StorageKeys.userProfile(this.userId);
        const data = await this.adapter.get<UserProfile>(key);
        
        if (data) {
            return {
                exists: true,
                timestamp: data.updatedAt || data.joinedDate
            };
        }
        
        return { exists: false };
    }

    /**
     * 从远端加载用户配置（兼容旧命名）
     */
    async loadUserProfileFromRemote(userId: string): Promise<UserProfile | null> {
        try {
            const key = StorageKeys.userProfile(userId);
            const profile = await this.adapter.get<UserProfile>(key);
            
            if (profile) {
                memoryCache.set(key, profile);
                persistentCache.set(key, profile, TTL_PRESETS.MEDIUM);
                return profile;
            }
            
            return null;
        } catch (error) {
            console.error('[DataService] 从自托管后端加载用户配置失败:', error);
            return null;
        }
    }

    /**
     * 保存用户配置到远端（兼容旧命名）
     */
    async saveUserProfileToRemote(profile: UserProfile): Promise<boolean> {
        try {
            const profileWithTimestamp = {
                ...profile,
                updatedAt: new Date().toISOString()
            };
            const key = StorageKeys.userProfile(profile.id || this.userId);
            await this.adapter.set(key, profileWithTimestamp);
            return true;
        } catch (error) {
            console.error('[DataService] 保存用户配置到自托管后端失败:', error);
            return false;
        }
    }

    /**
     * 获取用餐计划
     */
    async getScheduleByDate(): Promise<ScheduleByDate> {
        const key = StorageKeys.userSchedule(this.userId);

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        if (this.isAnonymous()) {
            memoryCache.set(key, {});
            return {};
        }

        const saved = await this.adapter.get<ScheduleByDate>(key);
        const schedule = saved || {};
        memoryCache.set(key, schedule);
        return schedule;
    }

    /**
     * 保存用餐计划
     */
    async saveScheduleByDate(schedule: ScheduleByDate): Promise<void> {
        const key = StorageKeys.userSchedule(this.userId);
        console.log('[dataService] Saving scheduleByDate for key:', key);
        console.log('[dataService] Schedule data:', schedule);
        console.log('[dataService] Number of dates:', Object.keys(schedule).length);
        if (!this.isAnonymous()) {
            await this.adapter.set(key, schedule);
        }
        memoryCache.set(key, schedule);
        console.log('[dataService] Schedule saved successfully');
    }

    /**
     * 获取购物清单
     */
    async getShoppingList(): Promise<ShoppingItem[]> {
        const key = StorageKeys.userShopping(this.userId);

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        if (this.isAnonymous()) {
            memoryCache.set(key, []);
            return [];
        }

        const saved = await this.adapter.get<ShoppingItem[]>(key);
        const list = saved || [];
        memoryCache.set(key, list);
        return list;
    }

    /**
     * 保存购物清单
     */
    async saveShoppingList(list: ShoppingItem[]): Promise<void> {
        const key = StorageKeys.userShopping(this.userId);
        if (!this.isAnonymous()) {
            await this.adapter.set(key, list);
        }
        memoryCache.set(key, list);
    }

    // ========== 用户库存操作 ==========

    /**
     * 获取用户库存
     */
    async getUserInventory(): Promise<UserInventory> {
        const key = StorageKeys.userInventory(this.userId);

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        if (this.isAnonymous()) {
            memoryCache.set(key, {});
            return {};
        }

        const saved = await this.adapter.get<UserInventory>(key);
        const inventory = saved || {};
        memoryCache.set(key, inventory);
        return inventory;
    }

    /**
     * 保存用户库存
     */
    async saveUserInventory(inventory: UserInventory): Promise<void> {
        const key = StorageKeys.userInventory(this.userId);
        if (!this.isAnonymous()) {
            await this.adapter.set(key, inventory);
        }
        memoryCache.set(key, inventory);
    }

    /**
     * 更新库存条目
     */
    async updateInventoryItem(ingredientId: string, data: Partial<InventoryItem>): Promise<void> {
        return this.wrapOperation(async () => {
            const inventory = await this.getUserInventory();

            // Validate inventory data
            if (data.currentAmount !== undefined && data.currentAmount < 0) {
                throw new ValidationError('库存数量不能为负数', 'currentAmount', { value: data.currentAmount });
            }
            if (data.requiredAmount !== undefined && data.requiredAmount < 0) {
                throw new ValidationError('需求数量不能为负数', 'requiredAmount', { value: data.requiredAmount });
            }
            if (data.unit !== undefined && !['g', 'ml'].includes(data.unit)) {
                throw new ValidationError('单位必须是 "g" 或 "ml"', 'unit', { value: data.unit });
            }

            // Update or create inventory item
            if (!inventory[ingredientId]) {
                inventory[ingredientId] = {
                    currentAmount: 0,
                    requiredAmount: 0,
                    unit: 'g',
                    batches: [],
                };
            }

            inventory[ingredientId] = {
                ...inventory[ingredientId],
                ...data,
            };

            await this.saveUserInventory(inventory);
        }, 'updateInventoryItem', { ingredientId });
    }

    /**
     * 删除库存条目
     */
    async deleteInventoryItem(ingredientId: string): Promise<void> {
        const inventory = await this.getUserInventory();

        if (inventory[ingredientId]) {
            delete inventory[ingredientId];
            await this.saveUserInventory(inventory);
        }
    }

    /**
     * 添加库存批次
     */
    async addInventoryBatch(ingredientId: string, batch: Omit<StockBatch, 'id'>): Promise<void> {
        return this.wrapOperation(async () => {
            const inventory = await this.getUserInventory();

            // Validate batch data
            if (!batch.date || !batch.expirationDate) {
                throw new ValidationError('批次必须包含购买日期和过期日期', 'date');
            }

            const batchDate = new Date(batch.date);
            const expiryDate = new Date(batch.expirationDate);
            if (expiryDate <= batchDate) {
                throw new ValidationError('过期日期必须晚于购买日期', 'expirationDate', {
                    purchaseDate: batch.date,
                    expirationDate: batch.expirationDate
                });
            }

            // Calculate days left
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            // Create inventory item if it doesn't exist
            if (!inventory[ingredientId]) {
                inventory[ingredientId] = {
                    currentAmount: 0,
                    requiredAmount: 0,
                    unit: batch.unit,
                    batches: [],
                };
            }

            // Add batch with generated ID
            const batchId = `${ingredientId}:b${Date.now()}`;
            inventory[ingredientId].batches.push({
                ...batch,
                id: batchId,
                daysLeft,
            });

            // Update current amount
            inventory[ingredientId].currentAmount += batch.quantity;

            await this.saveUserInventory(inventory);
        }, 'addInventoryBatch', { ingredientId, batch });
    }

    /**
     * 删除库存批次
     */
    async deleteInventoryBatch(ingredientId: string, batchId: string): Promise<void> {
        const inventory = await this.getUserInventory();

        if (!inventory[ingredientId]) {
            throw new Error(`Inventory item ${ingredientId} not found`);
        }

        const batchIndex = inventory[ingredientId].batches.findIndex(b => b.id === batchId);
        if (batchIndex === -1) {
            throw new Error(`Batch ${batchId} not found`);
        }

        const batch = inventory[ingredientId].batches[batchIndex];

        // Update current amount
        inventory[ingredientId].currentAmount -= batch.quantity;
        if (inventory[ingredientId].currentAmount < 0) {
            inventory[ingredientId].currentAmount = 0;
        }

        // Remove batch
        inventory[ingredientId].batches.splice(batchIndex, 1);

        await this.saveUserInventory(inventory);
    }

    // ========== 用户自定义食谱操作 ==========

    /**
     * 获取用户自定义食谱
     */
    async getUserRecipes(): Promise<UserRecipe[]> {
        const key = StorageKeys.userRecipes(this.userId);

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        if (this.isAnonymous()) {
            memoryCache.set(key, []);
            return [];
        }

        const saved = await this.adapter.get<UserRecipe[]>(key);
        const recipes = saved || [];
        memoryCache.set(key, recipes);
        return recipes;
    }

    /**
     * 保存用户自定义食谱
     */
    async saveUserRecipes(recipes: UserRecipe[]): Promise<void> {
        const key = StorageKeys.userRecipes(this.userId);
        await this.adapter.set(key, recipes);
        memoryCache.set(key, recipes);
    }

    async getRecipesByIngredient(ingredientId: string): Promise<Recipe[]> {
        const recipes = await this.adapter.findRecipesByIngredient(ingredientId);
        return recipes;
    }

    /**
     * 创建用户食谱
     */
    async createUserRecipe(recipe: Omit<UserRecipe, 'id' | 'ownerId' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<UserRecipe> {
        return this.wrapOperation(async () => {
            // Validate recipe data
            if (!recipe.name || !recipe.time || !recipe.difficulty) {
                throw new ValidationError('食谱必须包含名称、时间和难度信息');
            }

            // Validate ingredient references
            const allIngredients = await this.getAllIngredients();
            const validIngredientIds = new Set(allIngredients.map(i => i.id));

            const invalidIngredients: string[] = [];
            for (const ingredient of recipe.ingredients || []) {
                if (ingredient.linkedIngredientId && !validIngredientIds.has(ingredient.linkedIngredientId)) {
                    invalidIngredients.push(ingredient.linkedIngredientId);
                }
            }

            if (invalidIngredients.length > 0) {
                throw new ValidationError(
                    `食材不存在: ${invalidIngredients.join(', ')}`,
                    'ingredients',
                    { invalidIngredients }
                );
            }

            // Generate recipe ID using timestamp to avoid scanning all recipes for sequence
            const recipeId = `${this.userId}:r${Date.now()}`;

            // Create user recipe
            const userRecipe: UserRecipe = {
                ...recipe,
                id: recipeId,
                userId: this.userId,
                ownerId: this.userId,
                isPublic: false,
            };

            const key = StorageKeys.userRecipes(this.userId);
            await this.adapter.addRecord(key, userRecipe);

            // Invalidate cache
            memoryCache.delete(key);

            return userRecipe;
        }, 'createUserRecipe', { recipe });
    }

    /**
     * 更新用户食谱
     */
    async updateUserRecipe(recipeId: string, updates: Partial<UserRecipe>): Promise<void> {
        // Validate recipe ID format
        if (!recipeId.startsWith(`${this.userId}:r`)) {
            throw new Error('Recipe ID must belong to current user');
        }

        // We can optimize this by skipping fetching if we trust ID access, but validations need it?
        // Let's assume ID check is enough for now, or fetch single.
        // But adapter doesn't support getRecord(id), only get(key) list.
        // We will trust updates.

        // Validate updates
        if (updates.ingredients) {
            const allIngredients = await this.getAllIngredients();
            const validIngredientIds = new Set(allIngredients.map(i => i.id));

            for (const ingredient of updates.ingredients) {
                if (ingredient.linkedIngredientId && !validIngredientIds.has(ingredient.linkedIngredientId)) {
                    throw new Error(`Ingredient ${ingredient.linkedIngredientId} not found`);
                }
            }
        }

        const updateData = {
            ...updates,
            updatedAt: new Date().toISOString()
        };
        // Ensure critical fields are not overwritten invalidly if passed
        delete (updateData as any).id;
        delete (updateData as any).ownerId;

        const key = StorageKeys.userRecipes(this.userId);
        await this.adapter.updateRecord(key, recipeId, updateData);

        // Invalidate cache
        memoryCache.delete(key);
    }

    /**
     * 删除用户食谱
     */
    async deleteUserRecipe(recipeId: string): Promise<void> {
        // Validate recipe ID format
        if (!recipeId.startsWith(`${this.userId}:r`)) {
            throw new Error('Recipe ID must belong to current user');
        }

        const key = StorageKeys.userRecipes(this.userId);
        await this.adapter.deleteRecord(key, recipeId);

        // Invalidate cache
        memoryCache.delete(key);

    }

    // ========== 辅助方法 ==========

    /**
     * 获取所有食材（公共 + 用户自定义）
     */
    async getAllIngredients(): Promise<IngredientDetail[]> {
        const [publicIngredients, userIngredients, favorites] = await Promise.all([
            this.getIngredients(),   // 现在会先检查 L1 → L2 → L3
            this.getUserIngredients(),
            this.getUserFavorites(),
        ]);

        const favoriteIds = new Set(
            favorites.filter(f => f.type === 'ingredient').map(f => f.itemId)
        );

        return [
            ...publicIngredients, 
            ...userIngredients.map(ing => ({
                ...ing,
                nutrition: {
                    protein: ing.protein || 0,
                    fat: ing.fat || 0,
                    carbs: ing.carbs || 0,
                    fiber: ing.fiber || 0,
                },
                description: ing.description || '',
                image: ing.image || '',
                category: ing.category || '未分类',
                subCategory: ing.subCategory || '其他',
                calories: ing.calories || 0,
                unit: ing.unit || 'g',
            }) as IngredientDetail)
        ].map(ing => ({
            ...ing,
            isFavorite: favoriteIds.has(ing.id)
        }));
    }

    /**
     * 获取用户自定义食材
     */
    async getUserIngredients(): Promise<UserIngredient[]> {
        const key = StorageKeys.userIngredients(this.userId);

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        if (this.isAnonymous()) {
            memoryCache.set(key, []);
            return [];
        }

        const saved = await this.adapter.get<UserIngredient[]>(key);
        const ingredients = saved || [];
        memoryCache.set(key, ingredients);
        return ingredients;
    }

    /**
     * 保存用户自定义食材
     */
    async saveUserIngredients(ingredients: UserIngredient[]): Promise<void> {
        const key = StorageKeys.userIngredients(this.userId);
        await this.adapter.set(key, ingredients);
        memoryCache.set(key, ingredients);
    }

    /**
     * 创建用户自定义食材
     */
    async createUserIngredient(ingredient: Omit<UserIngredient, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>): Promise<UserIngredient> {
        const ingredients = await this.getUserIngredients();
        const publicIngredients = await this.getIngredients();

        // Validate ingredient data
        if (!ingredient.name?.trim()) {
            throw new ValidationError('食材名称必填');
        }
        if (!ingredient.category?.trim()) {
            throw new ValidationError('食材分类必填');
        }
        if (ingredient.calories !== undefined && ingredient.calories < 0) {
            throw new ValidationError('卡路里不能为负数');
        }
        if (ingredient.unit && !['g', 'ml'].includes(ingredient.unit)) {
            throw new ValidationError('单位必须是 "g" 或 "ml"');
        }

        if (ingredients.length >= 100) {
            throw new BusinessError('你已达到自定义食材上限（100条），请优先复用已有食材或清理旧词条');
        }

        const normalizedName = this.normalizeIngredientName(ingredient.name);

        const duplicateUserIngredient = ingredients.find(ing =>
            this.getIngredientLookupTerms(ing).some(term => this.normalizeIngredientName(term) === normalizedName)
        );
        if (duplicateUserIngredient) {
            throw new BusinessError(`已存在相近的自定义食材“${duplicateUserIngredient.name}”，请优先复用`);
        }

        const duplicatePublicIngredient = publicIngredients.find(ing =>
            this.getIngredientLookupTerms(ing).some(term => this.normalizeIngredientName(term) === normalizedName)
        );
        if (duplicatePublicIngredient) {
            throw new BusinessError(`已存在公共食材“${duplicatePublicIngredient.name}”，请优先选择已有词条`);
        }

        // Generate ID
        const sequence = ingredients.length + 1;
        const id = `${this.userId}:i${sequence}`;

        const now = new Date().toISOString();
        const newIngredient: UserIngredient = {
            ...ingredient,
            id,
            userId: this.userId,
            ownerId: this.userId,
            isPublic: false,
            createdAt: now,
            updatedAt: now,
        };

        ingredients.push(newIngredient);
        await this.saveUserIngredients(ingredients);

        return newIngredient;
    }

    /**
     * 更新用户自定义食材
     */
    async updateUserIngredient(ingredientId: string, updates: Partial<Omit<UserIngredient, 'id' | 'ownerId' | 'createdAt'>>): Promise<UserIngredient> {
        const ingredients = await this.getUserIngredients();
        const index = ingredients.findIndex(ing => ing.id === ingredientId);

        if (index === -1) {
            throw new Error(`Ingredient with ID ${ingredientId} not found`);
        }

        // Validate updates
        if (updates.name !== undefined && !updates.name.trim()) {
            throw new Error('Ingredient name cannot be empty');
        }
        if (updates.calories !== undefined && updates.calories < 0) {
            throw new Error('Calories cannot be negative');
        }
        if (updates.unit !== undefined && !['g', 'ml'].includes(updates.unit)) {
            throw new Error('Unit must be "g" or "ml"');
        }

        // Check for duplicate name (if name is being updated)
        if (updates.name && updates.name !== ingredients[index].name) {
            const duplicate = ingredients.find(
                ing => ing.name.toLowerCase() === updates.name!.toLowerCase() && ing.id !== ingredientId
            );
            if (duplicate) {
                throw new Error(`Ingredient with name "${updates.name}" already exists`);
            }
        }

        // Update ingredient
        ingredients[index] = {
            ...ingredients[index],
            ...updates,
            id: ingredientId, // Keep original ID
            ownerId: ingredients[index].ownerId, // Keep original owner
            createdAt: ingredients[index].createdAt, // Keep original creation time
            updatedAt: new Date().toISOString(),
        };

        await this.saveUserIngredients(ingredients);

        return ingredients[index];
    }

    /**
     * 删除用户自定义食材
     */
    async deleteUserIngredient(ingredientId: string): Promise<void> {
        const ingredients = await this.getUserIngredients();
        const index = ingredients.findIndex(ing => ing.id === ingredientId);

        if (index === -1) {
            throw new Error(`Ingredient with ID ${ingredientId} not found`);
        }

        // Check if ingredient is used in any recipes
        const allRecipes = await this.getAllRecipes();
        const usedInRecipes = allRecipes.filter(recipe =>
            recipe.ingredients?.some(ing => ing.linkedIngredientId === ingredientId)
        );

        if (usedInRecipes.length > 0) {
            throw new Error(
                `Cannot delete ingredient "${ingredients[index].name}" because it is used in ${usedInRecipes.length} recipe(s): ${usedInRecipes.map(r => r.name).join(', ')}`
            );
        }

        // Remove ingredient
        ingredients.splice(index, 1);
        await this.saveUserIngredients(ingredients);

        // Also remove from inventory if exists
        const inventory = await this.getUserInventory();
        if (inventory[ingredientId]) {
            delete inventory[ingredientId];
            await this.saveUserInventory(inventory);
        }
    }

    /**
     * 获取所有食谱（公共 + 用户自定义）
     */
    async getAllRecipes(): Promise<Recipe[]> {
        const [publicRecipes, userRecipes, favorites] = await Promise.all([
            this.getRecipes(),
            this.getUserRecipes(),
            this.getUserFavorites(),
        ]);

        const favoriteIds = new Set(
            favorites.filter(f => f.type === 'recipe').map(f => f.itemId)
        );

        const mergedRecipes = [
            ...publicRecipes, 
            ...userRecipes.map(r => ({
                ...r,
                time: r.time || '15 mins',
                difficulty: r.difficulty || 1,
                calories: r.calories || 0,
                image: r.image || '',
                tags: r.tags || [],
            }) as Recipe)
        ].map(r => ({
            ...r,
            isFavorite: favoriteIds.has(r.id)
        }));

        const recipesById = new Map<string, Recipe>();
        for (const recipe of mergedRecipes) {
            recipesById.set(recipe.id, recipe);
        }

        return Array.from(recipesById.values());
    }

    async getRecipeById(recipeId: string): Promise<Recipe | null> {
        const userRecipes = await this.getUserRecipes();
        const userRecipe = userRecipes.find((recipe) => recipe.id === recipeId);
        if (userRecipe) {
            return {
                ...userRecipe,
                time: userRecipe.time || '15 mins',
                difficulty: userRecipe.difficulty || 1,
                calories: userRecipe.calories || 0,
                image: userRecipe.image || '',
                tags: userRecipe.tags || [],
            } as Recipe;
        }

        return this.getPublicCatalogEntry<Recipe>('recipes', recipeId);
    }

    // ========== 收藏功能 ==========

    /**
     * 获取用户收藏列表
     */
    async getUserFavorites(): Promise<UserFavorite[]> {
        const key = StorageKeys.userFavorites(this.userId);
        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }
        if (this.isAnonymous()) {
            memoryCache.set(key, []);
            return [];
        }
        const saved = await this.adapter.get<UserFavorite[]>(key);
        const favorites = saved || [];
        memoryCache.set(key, favorites);
        return favorites;
    }

    /**
     * 添加到收藏
     */
    async addToFavorites(type: 'recipe' | 'ingredient', itemId: string): Promise<void> {
        const key = StorageKeys.userFavorites(this.userId);
        // MySQL DATETIME requires 'YYYY-MM-DD HH:MM:SS' format
        const now = new Date();
        const mysqlDatetime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        const favorite: UserFavorite = {
            id: `${this.userId}:${type}:${itemId}`,
            userId: this.userId,
            type,
            itemId,
            createdAt: mysqlDatetime
        };

        if (this.isAnonymous()) {
            const favorites = await this.getUserFavorites();
            favorites.push(favorite);
            memoryCache.set(key, favorites);
            return;
        }

        try {
            await this.adapter.addRecord(key, favorite);
            memoryCache.delete(key);
            // Also invalidate user profile cache to ensure favorites are refreshed there
            memoryCache.delete(StorageKeys.userProfile(this.userId));
        } catch (e) {
            console.warn('addToFavorites failed (possibly duplicate)', e);
        }
    }

    /**
     * 从收藏中移除
     */
    async removeFromFavorites(type: 'recipe' | 'ingredient', itemId: string): Promise<void> {
        const key = StorageKeys.userFavorites(this.userId);
        const favorites = await this.getUserFavorites();
        const fav = favorites.find(f => f.type === type && f.itemId === itemId);

        if (fav) {
            if (this.isAnonymous()) {
                const updated = favorites.filter(f => f.id !== fav.id);
                memoryCache.set(key, updated);
                return;
            }
            await this.adapter.deleteRecord(key, fav.id);
            memoryCache.delete(key);
            // Also invalidate user profile cache to ensure favorites are refreshed there
            memoryCache.delete(StorageKeys.userProfile(this.userId));
        }
    }

    /**
     * 切换收藏状态
     */
    async toggleFavorite(type: 'recipe' | 'ingredient', itemId: string): Promise<boolean> {
        const isFav = await this.isFavorite(type, itemId);
        if (isFav) {
            await this.removeFromFavorites(type, itemId);
            return false;
        } else {
            await this.addToFavorites(type, itemId);
            return true;
        }
    }

    /**
     * 检查是否已收藏
     */
    async isFavorite(type: 'recipe' | 'ingredient', itemId: string): Promise<boolean> {
        const favorites = await this.getUserFavorites();
        return favorites.some(f => f.type === type && f.itemId === itemId);
    }

    /**
     * 获取收藏的食谱
     */
    async getFavoriteRecipes(): Promise<Recipe[]> {
        const favorites = await this.getUserFavorites();
        const recipeIds = new Set(favorites.filter(f => f.type === 'recipe').map(f => f.itemId));
        const allRecipes = await this.getAllRecipes();
        return allRecipes.filter(recipe => recipeIds.has(recipe.id));
    }

    /**
     * 获取收藏的食材
     */
    async getFavoriteIngredients(): Promise<IngredientDetail[]> {
        const favorites = await this.getUserFavorites();
        const ingredientIds = new Set(favorites.filter(f => f.type === 'ingredient').map(f => f.itemId));
        const allIngredients = await this.getAllIngredients();
        return allIngredients.filter(ingredient => ingredientIds.has(ingredient.id));
    }



    /**
     * 查询某个食谱使用了哪些食材
     */
    async getIngredientsByRecipe(recipeId: string): Promise<IngredientDetail[]> {
        const allRecipes = await this.getAllRecipes();
        const recipe = allRecipes.find(r => r.id === recipeId);

        if (!recipe || !recipe.ingredients) {
            return [];
        }

        const ingredientIds = recipe.ingredients.map(ing => ing.linkedIngredientId).filter(id => id !== undefined) as string[];
        const allIngredients = await this.getAllIngredients();

        return allIngredients.filter(ingredient => ingredientIds.includes(ingredient.id));
    }

    // ========== 用户日志操作 ==========

    /**
     * 获取用户日志
     */
    async getUserLogs(userId: string): Promise<LogEntry[]> {
        const key = StorageKeys.userLogs(userId);
        if (!userId || userId === 'anonymous') {
            memoryCache.set(key, []);
            return [];
        }
        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }
        const saved = await this.adapter.get<LogEntry[]>(key);
        const logs = saved || [];
        memoryCache.set(key, logs);
        return logs;
    }

    /**
     * 创建用户日志
     */
    async createUserLog(userId: string, log: Partial<LogEntry>): Promise<LogEntry> {
        const key = StorageKeys.userLogs(userId);
        const newLog = await this.adapter.addRecord<LogEntry>(key, log);

        // 更新缓存
        if (memoryCache.has(key)) {
            const logs = memoryCache.get(key);
            logs.push(newLog);
            memoryCache.set(key, logs);
        }

        return newLog;
    }

    /**
     * 删除用户日志
     */
    async deleteUserLog(userId: string, logId: string): Promise<void> {
        const key = StorageKeys.userLogs(userId);
        await this.adapter.deleteRecord(key, logId);

        // 更新缓存
        if (memoryCache.has(key)) {
            let logs = memoryCache.get(key);
            logs = logs.filter((l: LogEntry) => l.id !== logId);
            memoryCache.set(key, logs);
        }
    }

    /**
     * 标记日志为已读
     */
    async markLogsAsRead(userId: string, logIds: string[]): Promise<void> {
        const logs = await this.getUserLogs(userId);
        const updatedLogs = logs.map(log => {
            if (logIds.includes(log.id)) {
                return { ...log, isRead: true };
            }
            return log;
        });
        await this.adapter.set(StorageKeys.userLogs(userId), updatedLogs);
    }

    /**
     * 创建用户档案（注册后调用）
     */
    async createUserProfile(userId: string, email: string, nickname: string): Promise<void> {
        try {
            // 保存用户基本信息到 localStorage
            const userBasicInfo = {
                id: userId,
                email,
                nickname,
                emailVerified: false,
                createdAt: new Date().toISOString(),
            };
            await this.adapter.set(StorageKeys.userBasicInfo(userId), userBasicInfo);

            // 初始化用户档案
            const userProfile: UserProfile = {
                id: userId,
                name: nickname,
                level: 1,
                xp: 0,
                maxXp: 100,
                theme: 'light',
                joinedDate: new Date().toISOString().split('T')[0],
                favoriteRecipeIds: [],
                favoriteIngredientIds: [],
                goals: {
                    height: 170,
                    weight: 65,
                    targetCalories: 2000,
                    targetProtein: 60,
                    targetFat: 65,
                    targetCarbs: 250,
                    targetFiber: 25,
                },
                allergies: [],
                dietaryRestrictions: [],
            };
            await this.saveUserProfile(userProfile);

            // 初始化库存
            await this.adapter.set(StorageKeys.userInventory(userId), {});

            // 初始化用餐计划
            await this.adapter.set(StorageKeys.userSchedule(userId), {});

            // 初始化购物清单
            await this.adapter.set(StorageKeys.userShopping(userId), []);

            // 初始化收藏
            await this.adapter.set(StorageKeys.userFavorites(userId), []);

            // 初始化自定义食谱和食材
            await this.adapter.set(StorageKeys.userRecipes(userId), []);
            await this.adapter.set(StorageKeys.userIngredients(userId), []);

            console.log('[DataService] 用户档案创建成功:', userId);
        } catch (error) {
            console.error('[DataService] 创建用户档案失败:', error);
            throw error;
        }
    }

    /**
     * 获取用户基本信息
     */
    async getUserBasicInfo(userId: string): Promise<{
        id: string;
        email: string;
        nickname: string;
        avatarUrl?: string;
        gender?: string;
        birthday?: string;
        bio?: string;
        emailVerified: boolean;
        createdAt: string;
        lastLoginAt?: string;
    } | null> {
        const key = StorageKeys.userBasicInfo(userId);
        return await this.adapter.get(key);
    }

    /**
     * 更新用户基本信息
     */
    async updateUserBasicInfo(userId: string, updates: Partial<{
        nickname: string;
        avatarUrl: string;
        gender: string;
        birthday: string;
        bio: string;
    }>): Promise<void> {
        const key = StorageKeys.userBasicInfo(userId);
        const current = await this.adapter.get(key);
        if (current) {
            await this.adapter.set(key, { ...current, ...updates });
        }
    }

    /**
     * 获取用户身体数据
     */
    async getUserBodyData(userId: string): Promise<{
        height?: number;
        weight?: number;
        targetWeight?: number;
        activityLevel?: string;
        bmr?: number;
        tdee?: number;
    } | null> {
        const key = StorageKeys.userBodyData(userId);
        return await this.adapter.get(key);
    }

    /**
     * 更新用户身体数据
     */
    async updateUserBodyData(userId: string, data: {
        height?: number;
        weight?: number;
        targetWeight?: number;
        activityLevel?: string;
        bmr?: number;
        tdee?: number;
    }): Promise<void> {
        const key = StorageKeys.userBodyData(userId);
        const current = await this.adapter.get<any>(key);
        const updatedData = { ...(current || {}), ...data };
        await this.adapter.set(key, updatedData);

        await this.updateUserProfileProjection(userId, (profile) => ({
            ...profile,
            goals: {
                ...profile.goals,
                height: updatedData.height ?? profile.goals.height,
                weight: updatedData.weight ?? profile.goals.weight,
            },
            bmr: updatedData.bmr ?? profile.bmr,
            tdee: updatedData.tdee ?? profile.tdee,
            activityLevel: updatedData.activityLevel ?? profile.activityLevel,
            updatedAt: new Date().toISOString(),
        }));
    }

    /**
     * 获取用户饮食偏好
     */
    async getUserDietaryPreferences(userId: string): Promise<{
        tastePreference?: string[];
        cuisinePreference?: string[];
        dislikedIngredients?: string[];
        allergens?: string[];
        dietaryRestrictions?: string[];
        mealFrequency?: number;
        snackPreference?: boolean;
        cookingSkillLevel?: string;
    } | null> {
        const key = StorageKeys.userDietaryPreferences(userId);
        return await this.adapter.get(key);
    }

    /**
     * 更新用户饮食偏好
     */
    async updateUserDietaryPreferences(userId: string, preferences: {
        tastePreference?: string[];
        cuisinePreference?: string[];
        dislikedIngredients?: string[];
        allergens?: string[];
        dietaryRestrictions?: string[];
        mealFrequency?: number;
        snackPreference?: boolean;
        cookingSkillLevel?: string;
    }): Promise<void> {
        const key = StorageKeys.userDietaryPreferences(userId);
        const current = await this.adapter.get<any>(key);
        const updatedData = { ...(current || {}), ...preferences };
        await this.adapter.set(key, updatedData);

        await this.updateUserProfileProjection(userId, (profile) => ({
            ...profile,
            allergies: updatedData.allergens || updatedData.allergies || [],
            dietaryRestrictions: updatedData.dietaryRestrictions || [],
            tastePreferences: updatedData.tastePreference || profile.tastePreferences,
            cuisinePreferences: updatedData.cuisinePreference || profile.cuisinePreferences,
            updatedAt: new Date().toISOString(),
        }));
    }

    /**
     * 获取用户营养目标
     */
    async getUserNutritionGoals(userId: string): Promise<{
        targetCalories?: number;
        targetProtein?: number;
        targetFat?: number;
        targetCarbs?: number;
        targetFiber?: number;
        goalType?: string;
        startDate?: string;
        endDate?: string;
    } | null> {
        const key = StorageKeys.userNutritionGoals(userId);
        return await this.adapter.get(key);
    }

    /**
     * 更新用户营养目标
     */
    async updateUserNutritionGoals(userId: string, goals: {
        targetCalories?: number;
        targetProtein?: number;
        targetFat?: number;
        targetCarbs?: number;
        targetFiber?: number;
        goalType?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<void> {
        const key = StorageKeys.userNutritionGoals(userId);
        const current = await this.adapter.get<any>(key);
        const updatedGoals = { ...(current || {}), ...goals };
        await this.adapter.set(key, updatedGoals);

        await this.updateUserProfileProjection(userId, (profile) => ({
            ...profile,
            goals: {
                ...profile.goals,
                targetCalories: updatedGoals.targetCalories ?? profile.goals.targetCalories,
                targetProtein: updatedGoals.targetProtein ?? profile.goals.targetProtein,
                targetFat: updatedGoals.targetFat ?? profile.goals.targetFat,
                targetCarbs: updatedGoals.targetCarbs ?? profile.goals.targetCarbs,
                targetFiber: updatedGoals.targetFiber ?? profile.goals.targetFiber,
            },
            goalType: updatedGoals.goalType ?? profile.goalType,
            updatedAt: new Date().toISOString(),
        }));
    }

    /**
     * 获取完整用户档案
     */
    async getCompleteUserProfile(userId: string): Promise<{
        basicInfo: any;
        bodyData: any;
        dietaryPreferences: any;
        nutritionGoals: any;
        userProfile: UserProfile;
    }> {
        const [basicInfo, bodyData, dietaryPreferences, nutritionGoals, userProfile] = await Promise.all([
            this.getUserBasicInfo(userId),
            this.getUserBodyData(userId),
            this.getUserDietaryPreferences(userId),
            this.getUserNutritionGoals(userId),
            this.getUserProfile(),   // 现在会先检查 L1 → L2 → L3
        ]);

        return {
            basicInfo: basicInfo || {},
            bodyData: bodyData || {},
            dietaryPreferences: dietaryPreferences || {},
            nutritionGoals: nutritionGoals || {},
            userProfile,
        };
    }

    // ========== 自定义时段配置 ==========

    /**
     * 获取用户自定义时段配置
     */
    async getUserMealTimeConfigs(): Promise<UserMealTimeConfig[]> {
        const key = StorageKeys.userSettings(this.userId);
        if (this.isAnonymous()) {
            return [];
        }
        const settings = await this.adapter.get<{ mealTimeConfigs?: UserMealTimeConfig[] }>(key);
        return settings?.mealTimeConfigs || [];
    }

    /**
     * 保存用户自定义时段配置
     */
    async saveUserMealTimeConfigs(configs: UserMealTimeConfig[]): Promise<void> {
        const key = StorageKeys.userSettings(this.userId);
        if (this.isAnonymous()) {
            return;
        }
        const current = await this.adapter.get<{ mealTimeConfigs?: UserMealTimeConfig[] }>(key) || {};
        await this.adapter.set(key, { ...current, mealTimeConfigs: configs });
    }

    /**
     * 添加自定义时段
     */
    async addMealTimeConfig(config: Omit<UserMealTimeConfig, 'id'>): Promise<UserMealTimeConfig> {
        const configs = await this.getUserMealTimeConfigs();
        const newConfig: UserMealTimeConfig = {
            ...config,
            id: `mt-${Date.now()}`,
            isDefault: false,
            sortOrder: configs.length + 1,
        };
        configs.push(newConfig);
        await this.saveUserMealTimeConfigs(configs);
        return newConfig;
    }

    /**
     * 更新自定义时段
     */
    async updateMealTimeConfig(id: string, updates: Partial<UserMealTimeConfig>): Promise<void> {
        const configs = await this.getUserMealTimeConfigs();
        const index = configs.findIndex(c => c.id === id);
        if (index !== -1) {
            configs[index] = { ...configs[index], ...updates };
            await this.saveUserMealTimeConfigs(configs);
        }
    }

    /**
     * 删除自定义时段
     */
    async deleteMealTimeConfig(id: string): Promise<void> {
        const configs = await this.getUserMealTimeConfigs();
        const filtered = configs.filter(c => c.id !== id);
        // 重新排序
        filtered.forEach((c, i) => { c.sortOrder = i + 1; });
        await this.saveUserMealTimeConfigs(filtered);
    }

    // ========== 计划统计查询 ==========

    /**
     * 获取指定日期范围的计划数据
     */
    async getScheduleByDateRange(startDate: string, endDate: string): Promise<ScheduleByDate> {
        const allSchedule = await this.getScheduleByDate();
        const result: ScheduleByDate = {};

        const start = new Date(startDate);
        const end = new Date(endDate);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = d.toISOString().split('T')[0];
            if (allSchedule[dateStr]) {
                result[dateStr] = allSchedule[dateStr];
            }
        }

        return result;
    }

    /**
     * 清除缓存
     */
    clearCache(): void {
        memoryCache.clear();
        persistentCache.clear();
    }

    /**
     * 清除所有数据并重置为初始状态
     */
    async resetAll(): Promise<void> {
        await this.adapter.clear();
        this.clearCache();
    }

    /**
     * 同步所有数据到存储
     */
    async syncAll(data: {
        recipes?: Recipe[];
        ingredients?: IngredientDetail[];
        userProfile?: UserProfile;
        scheduleByDate?: ScheduleByDate;
        shoppingList?: ShoppingItem[];
    }): Promise<void> {
        const entries = new Map<string, any>();

        if (data.recipes) {
            entries.set(StorageKeys.PUBLIC_RECIPES, data.recipes);
        }
        if (data.ingredients) {
            entries.set(StorageKeys.PUBLIC_INGREDIENTS, data.ingredients);
        }
        if (data.userProfile) {
            entries.set(StorageKeys.userProfile(this.userId), data.userProfile);
        }
        if (data.scheduleByDate) {
            entries.set(StorageKeys.userSchedule(this.userId), data.scheduleByDate);
        }
        if (data.shoppingList) {
            entries.set(StorageKeys.userShopping(this.userId), data.shoppingList);
        }

        await this.adapter.setMany(entries);

        // 更新缓存
        for (const [key, value] of entries) {
            memoryCache.set(key, value);
        }
    }

    /**
     * 加载所有数据
     */
    async loadAll(): Promise<{
        recipes: Recipe[];
        ingredients: IngredientDetail[];
        userProfile: UserProfile;
        scheduleByDate: ScheduleByDate;
        shoppingList: ShoppingItem[];
    }> {
        console.log('[DataService] loadAll: Starting data load');
        
        // 并行加载所有数据（缓存会自动处理）
        const [recipes, ingredients, userProfile, scheduleByDate, shoppingList] = await Promise.all([
            this.getAllRecipes(), // 使用 getAllRecipes 加载公共+用户食谱
            this.getIngredients(),   // 现在会先检查 L1 → L2 → L3
            this.getUserProfile(),   // 现在会先检查 L1 → L2 → L3
            this.getScheduleByDate(),
            this.getShoppingList(),
        ]);

        return {
            recipes,
            ingredients,
            userProfile,
            scheduleByDate,
            shoppingList,
        };
    }

    /**
     * 加载公开数据。
     * 公开食谱与食材应当优先可见，不能被用户态恢复过程拖挂。
     */
    async loadPublicData(): Promise<{
        recipes: Recipe[];
        ingredients: IngredientDetail[];
    }> {
        const [recipes, ingredients] = await Promise.all([
            this.getRecipes(),
            this.getIngredients(),
        ]);

        return {
            recipes,
            ingredients,
        };
    }

    // ========== AI配置操作 ==========

    /**
     * 清理旧的双倍前缀key（修复数据迁移）
     */
    private async cleanupOldAIConfigKey(): Promise<void> {
        try {
            const oldKey = `foodbook:foodbook:user:${this.userId}:ai_config`;
            const newKey = StorageKeys.userAIConfig(this.userId);

            // 检查是否存在旧的key
            const oldData = await localStorageAdapter.get<AIConfig>(oldKey);
            if (oldData) {
                console.log('[DataService] Found old AI config with duplicate prefix, migrating to new key...');
                // 迁移到新的key
                await localStorageAdapter.set(newKey, oldData);
                memoryCache.set(newKey, oldData);
                // 删除旧的key
                await localStorageAdapter.delete(oldKey);
                console.log('[DataService] Migration completed');
            }
        } catch (error) {
            console.warn('[DataService] Failed to cleanup old AI config:', error);
        }
    }

    /**
     * 获取用户AI配置
     * 注意：AI配置存储在localStorage中，因为包含敏感信息（API Key）
     */
    async getAIConfig(): Promise<AIConfig | null> {
        const key = StorageKeys.userAIConfig(this.userId);

        // 首次加载时尝试清理旧key
        if (!memoryCache.has(key)) {
            await this.cleanupOldAIConfigKey();
        }

        if (memoryCache.has(key)) {
            return memoryCache.get(key);
        }

        // 使用 localStorage 存储敏感数据
        const saved = await localStorageAdapter.get<AIConfig>(key);
        console.log('[DataService] Loading AI config from key:', key, 'Result:', saved);
        if (saved) {
            memoryCache.set(key, saved);
        }
        return saved;
    }

    /**
     * 保存用户AI配置
     * 注意：AI配置存储在localStorage中，因为包含敏感信息（API Key）
     */
    async saveAIConfig(config: AIConfig): Promise<void> {
        const key = StorageKeys.userAIConfig(this.userId);
        console.log('[DataService] Saving AI config to key:', key, 'Config:', config);
        // 使用 localStorage 存储敏感数据
        await localStorageAdapter.set(key, config);
        memoryCache.set(key, config);
    }

    /**
     * 更新用户AI配置
     */
    async updateAIConfig(updates: Partial<AIConfig>): Promise<AIConfig> {
        const current = await this.getAIConfig();
        const updated: AIConfig = {
            ...(current || {}),
            ...updates,
            apiKey: updates.apiKey || current?.apiKey || '',
            modelType: updates.modelType || current?.modelType || 'openai',
            enabled: updates.enabled !== undefined ? updates.enabled : (current?.enabled ?? false),
        };
        await this.saveAIConfig(updated);
        return updated;
    }

    /**
     * 删除用户AI配置
     */
    async deleteAIConfig(): Promise<void> {
        const key = StorageKeys.userAIConfig(this.userId);
        // 使用 localStorage 删除敏感数据
        await localStorageAdapter.delete(key);
        memoryCache.delete(key);
    }

    /**
     * 检查AI功能是否可用
     */
    async isAIEnabled(): Promise<boolean> {
        const config = await this.getAIConfig();
        return config?.enabled === true && !!config.apiKey;
    }

    /**
     * 获取食谱分类体系
     * 返回9大一级分类及其子分类
     */
    async getRecipeTaxonomy(): Promise<{ id: string; label: string; icon: string; subCategories: string[] }[]> {
        // 返回新的分类体系
        return RECIPE_TAG_TAXONOMY.map(cat => ({
            id: cat.id,
            label: cat.label,
            icon: cat.icon,
            subCategories: cat.subCategories
        }));
    }

    // ========== 缓存管理方法 ==========

    /**
     * 获取缓存统计信息（用于开发者后台）
     */
    getCacheStats(): {
        memory: any;
        persistent: any;
    } {
        return {
            memory: memoryCache.getStats(),
            persistent: persistentCache.getStats()
        };
    }

    /**
     * 清除所有缓存（用于强制刷新）
     */
    clearAllCaches(): void {
        memoryCache.clear();
        persistentCache.clear();
        console.log('[DataService] All caches cleared');
    }

    /**
     * 清除过期缓存
     * @returns 清理的条目数量
     */
    cleanupExpiredCache(): number {
        const count = persistentCache.cleanup();
        memoryCache.cleanup();
        return count;
    }
}

// 导出单例实例
export const dataService = new DataService(storageAdapter);

// 导出类以供测试或自定义实例化
export { DataService };

